import { Box } from "@chakra-ui/react";
import { ColorIcon, Spacing } from "goi_common";
import { baseColors, BaseStack } from "goi_common/src/design-system";
import { GUIDE_PARLOR_ITEMS, GUIDE_POINTS } from "./sangjoLandingData";
import { Accent, Eyebrow, GraphicIcon, LandingSection, SectionTitle } from "./SangjoLandingUI";

export default function SangjoLandingGuide() {
  return (
    <LandingSection paddingBottom={34}>
      <Eyebrow>상조, 이것만 알면 돼요</Eyebrow>
      <Spacing gap={8} />
      <SectionTitle>
        상조는 장례 진행을
        <br />
        대신 맡아주는 서비스예요
      </SectionTitle>

      <Spacing gap={40} />
      <Box className="headline_2_sb" color={baseColors.label_normal}>
        장례식 비용은 크게 세 가지예요
      </Box>
      <Spacing gap={16} />

      <Box p="16px" borderRadius="12px" bg={baseColors.background_accent_1}>
        <Box className="label_1_m" color={baseColors.label_assistive_1} pb="12px">
          장례식장에 내는 비용
        </Box>
        <Box display="grid" gridTemplateColumns="1fr 1fr" gap="8px">
          {GUIDE_PARLOR_ITEMS.map((item) => (
            <CostItem key={item.title} {...item} bg={baseColors.background_normal} />
          ))}
        </Box>
      </Box>

      <Spacing gap={8} />
      <Box
        p="16px"
        borderRadius="12px"
        bg={baseColors.background_normal}
        boxShadow={`inset 0 0 0 1px ${baseColors.orange_200}`}
      >
        <Box className="label_1_sb" color={baseColors.label_normal} pb="12px">
          장례식장 또는 상조 중 선택
        </Box>
        <Box position="relative">
          <CostItem
            icon="helper"
            title="장례 진행 비용"
            desc="진행에 필요한 인력, 용품, 차량"
            bg={baseColors.background_accent_1}
          />
          <DownArrow />
        </Box>
      </Box>

      <Spacing gap={56} />
      <Box className="headline_1_sb" color={baseColors.label_normal} textAlign="center">
        처음 안내받은 장례 비용,
        <br />
        <Accent>실제로 내는 금액도 같을까요?</Accent>
      </Box>

      <Spacing gap={24} />
      <CompareBars />

      <Spacing gap={32} />
      <BaseStack>
        {GUIDE_POINTS.map((point, i) => (
          <Box
            key={point.title}
            py="20px"
            borderTop={i === 0 ? "none" : `1px solid ${baseColors.line_normal}`}
          >
            <BaseStack direction="horizontal" spacing={6} alignItems="center">
              <ColorIcon
                name={
                  point.tone === "good"
                    ? "icon_normal_check"
                    : "icon_normal_circle_exclamation_line"
                }
                size={20}
                color={point.tone === "good" ? baseColors.primary_orange : baseColors.label_muted_2}
              />
              <Box className="body_1_normal_sb" color={baseColors.label_normal}>
                {point.title}
              </Box>
            </BaseStack>
            <Box
              className="body_3_reading_r"
              color={baseColors.label_assistive_1}
              pl="26px"
              pt="4px"
            >
              {point.desc}
            </Box>
          </Box>
        ))}
      </BaseStack>
    </LandingSection>
  );
}

const CostItem = ({
  icon,
  title,
  desc,
  bg,
}: typeof GUIDE_PARLOR_ITEMS[number] & { bg: string }) => (
  <BaseStack p="12px" borderRadius="6px" bg={bg} spacing={10}>
    <GraphicIcon name={icon} size={32} />
    <Box>
      <Box className="body_1_normal_sb" color={baseColors.label_normal}>
        {title}
      </Box>
      <Box className="label_2_r" color={baseColors.label_assistive_1}>
        {desc}
      </Box>
    </Box>
  </BaseStack>
);

/** 상조 선택 박스 하단 중앙에 붙는 그라데이션 화살표(장식) */
const DownArrow = () => (
  <BaseStack
    position="absolute"
    top="100%"
    left="50%"
    transform="translateX(-50%)"
    alignItems="center"
    pointerEvents="none"
    zIndex={1}
  >
    <Box
      width="10px"
      height="36px"
      bg={`linear-gradient(180deg, ${baseColors.orange_100}, ${baseColors.orange_400})`}
    />
    <Box
      width={0}
      height={0}
      borderLeft="18px solid transparent"
      borderRight="18px solid transparent"
      borderTop={`18px solid ${baseColors.orange_400}`}
    />
  </BaseStack>
);

const CompareBars = () => (
  <Box>
    <Box
      display="grid"
      gridTemplateColumns="repeat(3, 1fr)"
      gap="12px"
      height="168px"
      px="8px"
      alignItems="end"
      borderBottom={`1px solid ${baseColors.label_normal}`}
    >
      <Bar heightPct={78} bg={baseColors.fill_accent}>
        <Box className="label_2_m" color={baseColors.label_alternative_1}>
          식장 진행 비용
        </Box>
      </Bar>

      <BaseStack height="100%" justifyContent="flex-end">
        <Box
          height="56px"
          border={`1px dashed ${baseColors.line_accent}`}
          borderBottom="none"
          borderRadius="6px 6px 0 0"
          display="flex"
          alignItems="center"
          justifyContent="center"
          textAlign="center"
          className="label_2_m"
          color={baseColors.label_assistive_1}
        >
          + 현장 추가금
        </Box>
        <Box
          height="40%"
          bg={baseColors.fill_normal}
          display="flex"
          alignItems="center"
          justifyContent="center"
          className="label_2_m"
          color={baseColors.label_alternative_1}
        >
          시작가
        </Box>
      </BaseStack>

      <Bar heightPct={62} bg={baseColors.primary_orange}>
        <Box className="label_2_sb" color={baseColors.white}>
          미리 안내한
          <br />
          금액 그대로
        </Box>
      </Bar>
    </Box>

    <Box display="grid" gridTemplateColumns="repeat(3, 1fr)" gap="12px" px="8px" pt="12px">
      <Box className="body_2_normal_m" color={baseColors.label_assistive_1} textAlign="center">
        장례식장
      </Box>
      <Box className="body_2_normal_m" color={baseColors.label_assistive_1} textAlign="center">
        저가 상조
      </Box>
      <Box className="body_2_normal_m" color={baseColors.primary_orange} textAlign="center">
        고이 상조
      </Box>
    </Box>
  </Box>
);

const Bar = ({
  heightPct,
  bg,
  children,
}: {
  heightPct: number;
  bg: string;
  children: React.ReactNode;
}) => (
  <Box
    height={`${heightPct}%`}
    bg={bg}
    borderRadius="6px 6px 0 0"
    display="flex"
    alignItems="center"
    justifyContent="center"
    textAlign="center"
  >
    {children}
  </Box>
);
